import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { masterDataService } from '../services/master-data-service';
import {
  Currency,
  CreateCurrencyData,
  UpdateCurrencyData,
  LOB,
  CreateLOBData,
  UpdateLOBData,
  TaxConfig,
  CreateTaxConfigData,
  UpdateTaxConfigData,
  RateCard,
  CreateRateCardData,
  UpdateRateCardData,
  Country,
  CreateCountryData,
  UpdateCountryData,
  QuoteType,
  CreateQuoteTypeData,
  UpdateQuoteTypeData,
} from '../types/master-data.types';

/**
 * Estado de datos maestros
 */
interface MasterDataState {
  // Estado
  currencies: Currency[];
  lobs: LOB[];
  taxConfigs: TaxConfig[];
  rateCards: RateCard[];
  countries: Country[];
  quoteTypes: QuoteType[];
  isLoading: boolean;
  isSyncing: boolean;
  error: string | null;

  // Acciones - Currencies
  fetchCurrencies: () => Promise<void>;
  syncExchangeRates: () => Promise<void>;
  createCurrency: (data: CreateCurrencyData) => Promise<Currency>;
  updateCurrency: (id: string, data: UpdateCurrencyData) => Promise<Currency>;
  deleteCurrency: (id: string) => Promise<void>;

  // Acciones - LOBs
  fetchLOBs: () => Promise<void>;
  createLOB: (data: CreateLOBData) => Promise<LOB>;
  updateLOB: (id: string, data: UpdateLOBData) => Promise<LOB>;
  deleteLOB: (id: string, options?: { cascade?: boolean }) => Promise<void>;

  // Acciones - Tax Configs
  fetchTaxConfigs: () => Promise<void>;
  createTaxConfig: (data: CreateTaxConfigData) => Promise<TaxConfig>;
  updateTaxConfig: (id: string, data: UpdateTaxConfigData) => Promise<TaxConfig>;
  deleteTaxConfig: (id: string) => Promise<void>;

  // Acciones - Countries
  fetchCountries: () => Promise<void>;
  createCountry: (data: CreateCountryData) => Promise<Country>;
  updateCountry: (id: string, data: UpdateCountryData) => Promise<Country>;
  deleteCountry: (id: string) => Promise<void>;

  // Acciones - Rate Cards
  fetchRateCards: () => Promise<void>;
  createRateCard: (data: CreateRateCardData) => Promise<RateCard>;
  updateRateCard: (id: string, data: UpdateRateCardData) => Promise<RateCard>;
  deleteRateCard: (id: string) => Promise<void>;

  // Acciones - Quote Types
  fetchQuoteTypes: () => Promise<void>;
  createQuoteType: (data: CreateQuoteTypeData) => Promise<QuoteType>;
  updateQuoteType: (id: string, data: UpdateQuoteTypeData) => Promise<QuoteType>;
  deleteQuoteType: (id: string) => Promise<void>;

  // Helpers
  clearError: () => void;
}

export const useMasterDataStore = create<MasterDataState>()(
  devtools(
    (set, _get) => ({
      // Estado inicial
      currencies: [],
      lobs: [],
      taxConfigs: [],
      rateCards: [],
      countries: [],
      quoteTypes: [],
      isLoading: false,
      isSyncing: false,
      error: null,

      // ============ Currencies ============
      syncExchangeRates: async () => {
        try {
          set({ isSyncing: true });
          await masterDataService.syncExchangeRates();
          const currencies = await masterDataService.getCurrencies();
          set({ currencies, isSyncing: false });
        } catch (error: any) {
          set({ isSyncing: false });
          throw error;
        }
      },

      fetchCurrencies: async () => {
        try {
          set({ isLoading: true, error: null });
          const currencies = await masterDataService.getCurrencies();
          set({ currencies, isLoading: false });
        } catch (error: any) {
          set({
            error: error.response?.data?.message || 'Error al cargar divisas',
            isLoading: false,
          });
          throw error;
        }
      },

      createCurrency: async (data) => {
        try {
          set({ isLoading: true, error: null });
          const currency = await masterDataService.createCurrency(data);
          set((state) => ({
            currencies: [...state.currencies, currency],
            isLoading: false,
          }));
          return currency;
        } catch (error: any) {
          set({
            error: error.response?.data?.message || 'Error al crear divisa',
            isLoading: false,
          });
          throw error;
        }
      },

      updateCurrency: async (id, data) => {
        try {
          set({ isLoading: true, error: null });
          const currency = await masterDataService.updateCurrency(id, data);
          set((state) => ({
            currencies: state.currencies.map((c) => (c.id === id ? currency : c)),
            isLoading: false,
          }));
          return currency;
        } catch (error: any) {
          set({
            error: error.response?.data?.message || 'Error al actualizar divisa',
            isLoading: false,
          });
          throw error;
        }
      },

      deleteCurrency: async (id) => {
        try {
          set({ isLoading: true, error: null });
          await masterDataService.deleteCurrency(id);
          set((state) => ({
            currencies: state.currencies.filter((c) => c.id !== id),
            isLoading: false,
          }));
        } catch (error: any) {
          set({
            error: error.response?.data?.message || 'Error al eliminar divisa',
            isLoading: false,
          });
          throw error;
        }
      },

      // ============ LOBs ============
      fetchLOBs: async () => {
        try {
          set({ isLoading: true, error: null });
          const lobs = await masterDataService.getLOBs();
          set({ lobs, isLoading: false });
        } catch (error: any) {
          set({
            error: error.response?.data?.message || 'Error al cargar LOBs',
            isLoading: false,
          });
          throw error;
        }
      },

      createLOB: async (data) => {
        try {
          set({ isLoading: true, error: null });
          const lob = await masterDataService.createLOB(data);
          set((state) => ({
            lobs: [...state.lobs, lob],
            isLoading: false,
          }));
          return lob;
        } catch (error: any) {
          set({
            error: error.response?.data?.message || 'Error al crear LOB',
            isLoading: false,
          });
          throw error;
        }
      },

      updateLOB: async (id, data) => {
        try {
          set({ isLoading: true, error: null });
          const lob = await masterDataService.updateLOB(id, data);
          set((state) => ({
            lobs: state.lobs.map((l) => (l.id === id ? lob : l)),
            isLoading: false,
          }));
          return lob;
        } catch (error: any) {
          set({
            error: error.response?.data?.message || 'Error al actualizar LOB',
            isLoading: false,
          });
          throw error;
        }
      },

      deleteLOB: async (id, options) => {
        try {
          set({ isLoading: true, error: null });
          await masterDataService.deleteLOB(id, options);
          if (options?.cascade) {
            // Cascade soft-deletea descendientes recursivos: re-fetch para
            // sincronizar la jerarquia completa en lugar de un filter local.
            const lobs = await masterDataService.getLOBs();
            set({ lobs, isLoading: false });
          } else {
            set((state) => ({
              lobs: state.lobs.filter((l) => l.id !== id),
              isLoading: false,
            }));
          }
        } catch (error: any) {
          set({
            error: error.response?.data?.message || 'Error al eliminar LOB',
            isLoading: false,
          });
          throw error;
        }
      },

      // ============ Tax Configs ============
      fetchTaxConfigs: async () => {
        try {
          set({ isLoading: true, error: null });
          const taxConfigs = await masterDataService.getTaxConfigs();
          set({ taxConfigs, isLoading: false });
        } catch (error: any) {
          set({
            error: error.response?.data?.message || 'Error al cargar impuestos',
            isLoading: false,
          });
          throw error;
        }
      },

      createTaxConfig: async (data) => {
        try {
          set({ isLoading: true, error: null });
          const taxConfig = await masterDataService.createTaxConfig(data);
          set((state) => ({
            taxConfigs: [...state.taxConfigs, taxConfig],
            isLoading: false,
          }));
          return taxConfig;
        } catch (error: any) {
          set({
            error: error.response?.data?.message || 'Error al crear impuesto',
            isLoading: false,
          });
          throw error;
        }
      },

      updateTaxConfig: async (id, data) => {
        try {
          set({ isLoading: true, error: null });
          const taxConfig = await masterDataService.updateTaxConfig(id, data);
          set((state) => ({
            taxConfigs: state.taxConfigs.map((t) => (t.id === id ? taxConfig : t)),
            isLoading: false,
          }));
          return taxConfig;
        } catch (error: any) {
          set({
            error: error.response?.data?.message || 'Error al actualizar impuesto',
            isLoading: false,
          });
          throw error;
        }
      },

      deleteTaxConfig: async (id) => {
        try {
          set({ isLoading: true, error: null });
          await masterDataService.deleteTaxConfig(id);
          set((state) => ({
            taxConfigs: state.taxConfigs.filter((t) => t.id !== id),
            isLoading: false,
          }));
        } catch (error: any) {
          set({
            error: error.response?.data?.message || 'Error al eliminar impuesto',
            isLoading: false,
          });
          throw error;
        }
      },

      // ============ Countries ============
      fetchCountries: async () => {
        try {
          set({ isLoading: true, error: null });
          const countries = await masterDataService.getCountries();
          set({ countries, isLoading: false });
        } catch (error: any) {
          set({
            error: error.response?.data?.message || 'Error al cargar países',
            isLoading: false,
          });
          throw error;
        }
      },

      createCountry: async (data) => {
        try {
          set({ isLoading: true, error: null });
          const country = await masterDataService.createCountry(data);
          set((state) => ({
            countries: [...state.countries, country],
            isLoading: false,
          }));
          return country;
        } catch (error: any) {
          set({
            error: error.response?.data?.message || 'Error al crear país',
            isLoading: false,
          });
          throw error;
        }
      },

      updateCountry: async (id, data) => {
        try {
          set({ isLoading: true, error: null });
          const country = await masterDataService.updateCountry(id, data);
          set((state) => ({
            countries: state.countries.map((c) => (c.id === id ? country : c)),
            isLoading: false,
          }));
          return country;
        } catch (error: any) {
          set({
            error: error.response?.data?.message || 'Error al actualizar país',
            isLoading: false,
          });
          throw error;
        }
      },

      deleteCountry: async (id) => {
        try {
          set({ isLoading: true, error: null });
          await masterDataService.deleteCountry(id);
          set((state) => ({
            countries: state.countries.filter((c) => c.id !== id),
            isLoading: false,
          }));
        } catch (error: any) {
          set({
            error: error.response?.data?.message || 'Error al eliminar país',
            isLoading: false,
          });
          throw error;
        }
      },

      // ============ Rate Cards ============
      fetchRateCards: async () => {
        try {
          set({ isLoading: true, error: null });
          const rateCards = await masterDataService.getRateCards();
          set({ rateCards, isLoading: false });
        } catch (error: any) {
          set({
            error: error.response?.data?.message || 'Error al cargar tarifarios',
            isLoading: false,
          });
          throw error;
        }
      },

      createRateCard: async (data) => {
        try {
          set({ isLoading: true, error: null });
          const rateCard = await masterDataService.createRateCard(data);
          set((state) => ({
            rateCards: [...state.rateCards, rateCard],
            isLoading: false,
          }));
          return rateCard;
        } catch (error: any) {
          set({
            error: error.response?.data?.message || 'Error al crear tarifario',
            isLoading: false,
          });
          throw error;
        }
      },

      updateRateCard: async (id, data) => {
        try {
          set({ isLoading: true, error: null });
          const rateCard = await masterDataService.updateRateCard(id, data);
          set((state) => ({
            rateCards: state.rateCards.map((r) => (r.id === id ? rateCard : r)),
            isLoading: false,
          }));
          return rateCard;
        } catch (error: any) {
          set({
            error: error.response?.data?.message || 'Error al actualizar tarifario',
            isLoading: false,
          });
          throw error;
        }
      },

      deleteRateCard: async (id) => {
        try {
          set({ isLoading: true, error: null });
          await masterDataService.deleteRateCard(id);
          set((state) => ({
            rateCards: state.rateCards.filter((r) => r.id !== id),
            isLoading: false,
          }));
        } catch (error: any) {
          set({
            error: error.response?.data?.message || 'Error al eliminar tarifario',
            isLoading: false,
          });
          throw error;
        }
      },

      // ============ Quote Types ============
      fetchQuoteTypes: async () => {
        try {
          set({ isLoading: true, error: null });
          const quoteTypes = await masterDataService.getQuoteTypes();
          set({ quoteTypes, isLoading: false });
        } catch (error: any) {
          set({
            error: error.response?.data?.message || 'Error al cargar tipos de cotización',
            isLoading: false,
          });
          throw error;
        }
      },

      createQuoteType: async (data) => {
        try {
          set({ isLoading: true, error: null });
          const quoteType = await masterDataService.createQuoteType(data);
          set((state) => ({
            quoteTypes: [...state.quoteTypes, quoteType],
            isLoading: false,
          }));
          return quoteType;
        } catch (error: any) {
          set({
            error: error.response?.data?.message || 'Error al crear tipo de cotización',
            isLoading: false,
          });
          throw error;
        }
      },

      updateQuoteType: async (id, data) => {
        try {
          set({ isLoading: true, error: null });
          const quoteType = await masterDataService.updateQuoteType(id, data);
          set((state) => ({
            quoteTypes: state.quoteTypes.map((q) => (q.id === id ? quoteType : q)),
            isLoading: false,
          }));
          return quoteType;
        } catch (error: any) {
          set({
            error: error.response?.data?.message || 'Error al actualizar tipo de cotización',
            isLoading: false,
          });
          throw error;
        }
      },

      deleteQuoteType: async (id) => {
        try {
          set({ isLoading: true, error: null });
          await masterDataService.deleteQuoteType(id);
          set((state) => ({
            quoteTypes: state.quoteTypes.filter((q) => q.id !== id),
            isLoading: false,
          }));
        } catch (error: any) {
          set({
            error: error.response?.data?.message || 'Error al eliminar tipo de cotización',
            isLoading: false,
          });
          throw error;
        }
      },

      // ============ Helpers ============
      clearError: () => {
        set({ error: null });
      },
    }),
    { name: 'MasterDataStore' }
  )
);

export default useMasterDataStore;
